import { Fragment, useCallback, useState, useRef } from 'react'
import classNames from 'classnames'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form/dist/index.ie11'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheck } from '@fortawesome/pro-regular-svg-icons'
import formatError from '../../../utilities/formatError'
import { formatPercentage, formatNumber } from '../utils'
import { Stack } from '../../stack'
import { Button } from '../../button'
import { TextField } from '../../textfield'
import { Select } from '../../select'
import { Alert } from '../../alert'
import { Row, Col } from '../../grid'
import { Heading } from '../../typography/Heading'
import computeOffsetTop from '../../../utilities/computeTopOffset'
import useEnabledOnClient from '../../../utilities/useEnabledOnClient'
import * as styles from '../CalculatorModule.module.scss'

// allowable loan terms (in years)
const LOAN_TERMS = [10, 15, 20, 25, 30]

const validationSchema = {
  loanAmount: { min: 0 },
  interestRate: {
    // min: 0,
    // max: 40,
  },
  termMonths: {
    min: 1,
    max: 360,
  },
  propertyValue: {
    min: 0,
    max: 999999999,
  },
  propertyTaxes: {
    min: 0,
    max: 999999999,
  },
  hazardInsurance: {
    min: 0,
    max: 999999999,
  },
  pmi: {
    min: 0,
    max: 999999999,
  },
}

const MonthlyPaymentCalculator = () => {
  const [t, i18n] = useTranslation()
  const [language, countryCode] = i18n.language.split('-')
  const [disabledField, setDisabledField] = useState(true)
  const [calcResults, setCalcResults] = useState()
  const [storedFormData, setStoredFormData] = useState()
  // Our API doesn’t support sync form submissions and so we need Javascript to submit the form asynchronously by issuing an XHR. Therefore, we need to ensure that the button is initially disabled and gets re-enabled once all Javascript has loaded on the client.
  const disabled = useEnabledOnClient()

  const { register, errors, handleSubmit } = useForm({
    mode: 'onBlur',
    defaultValues: {
      loanAmount: 0,
      interestRate: 6,
      termMonths: 360,
      propertyValue: 0,
      propertyTaxes: 0,
      hazardInsurance: 0,
      amortization: 2,
      pmi: 0,
    },
  })

  const [inProgress, setInProgress] = useState(false)
  const [alert, showAlert] = useState(null)
  const dismissAlert = useCallback(() => showAlert(null), [])
  const resultsContainer = useRef(null)
  const resultsHeader = useRef(null)

  const handleSubmitValid = async formData => {
    setInProgress(true)
    showAlert(false)
    setStoredFormData(formData)

    try {
      const url = 'https://www.calcxml.com/rest/hom03'

      // Documentation
      // https://www.calcxml.com/documentation/rest/Hom03JSONRequest.htm

      const payload = {
        username: process.env.CALCXML_USERNAME,
        password: process.env.CALCXML_PASSWORD,
        language,
        countryCode,
        chartWidth: 900,
        chartHeight: 400,
        barColor1: '63B5E4',
        barColor2: '84BD00',
        barColor3: 'FCC841',
        // barColor4: '#ffffff',
        // barColor5: '#ffffff',
        // barColor6: '#ffffff',
        loanAmount: formData.loanAmount,
        interestRate: formatPercentage(formData.interestRate),
        termMonths: formData.termMonths,
        amortization: formData.amortization,
        propertyValue: formData.propertyValue,
        estimateTipmi: formData.estimateTipmi,
        propertyTaxes: formData.propertyTaxes,
        hazardInsurance: formData.hazardInsurance,
        pmi: formData.pmi,
      }

      const response = await axios.post(url, payload)

      if (response.status !== 200 || Boolean(response.data.error)) {
        throw new Error(`Unexpected status code: ${response.status} ${response.statusText}`)
      }

      setInProgress(false)
      setCalcResults(response.data)
      window.scrollTo({
        top: computeOffsetTop(resultsContainer.current) - 20,
        behavior: 'smooth',
      })
      resultsHeader.current.focus()
    } catch (error) {
      setInProgress(false)
      showAlert({
        type: 'error',
        title: t('MonthlyPayment.error', {
          defaultValue: 'Error',
        }),
        message: error.message,
      })
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const setDisabledFields = e => {
    if (e.target.value === 'N') {
      setDisabledField(false)
    } else {
      setDisabledField(true)
    }
  }

  return (
    <div>
      {alert && (
        <Row>
          <Col xs={12}>
            <Alert variant={alert.type} title={alert.title} onDismiss={dismissAlert}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}
      <form
        action="/"
        method="POST"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleSubmitValid)}
        className={alert ? styles.HasAlert : ''}
      >
        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('MonthlyPayment.firstLegend', {
              defaultValue: 'Mortgage loan information',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('MonthlyPayment.loanAmountLabel', {
                  defaultValue: 'Mortgage loan amount (house price minus your down payment)',
                })}
                id="loanAmount"
                name="loanAmount"
                type="number"
                ref={register(validationSchema.loanAmount)}
                error={formatError(errors.loanAmount, validationSchema.loanAmount, t)}
                autoFocus
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <TextField
                label={t('MonthlyPayment.annualInterestRateLabel', {
                  defaultValue: 'Annual interest rate (%)',
                })}
                id="interestRate"
                name="interestRate"
                type="number"
                // min="0"
                // max="40"
                ref={register(validationSchema.interestRate)}
                error={formatError(errors.interestRate, validationSchema.interestRate, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <Select
                label={t('MonthlyPayment.numberOfMonthsLabel', {
                  defaultValue: 'Loan term (years)',
                })}
                id="termMonths"
                name="termMonths"
                ref={register(validationSchema.termMonths)}
                error={formatError(errors.termMonths, validationSchema.termMonths, t)}
              >
                {LOAN_TERMS.map(years => (
                  <Select.Option value={years * 12}>{years}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} lg={6}>
              <Select
                label={t('MonthlyPayment.desiredAmortizationLabel', {
                  defaultValue: 'Display monthly or yearly payment table?',
                })}
                id="amortization"
                name="amortization"
                ref={register(validationSchema.amortization)}
                error={formatError(errors.amortization, validationSchema.amortization, t)}
              >
                <Select.Option value="2">
                  {t('MonthlyPayment.amortization.yearly', {
                    defaultValue: 'Yearly',
                  })}
                </Select.Option>
                <Select.Option value="1">
                  {t('MonthlyPayment.amortization.monthly', {
                    defaultValue: 'Monthly',
                  })}
                </Select.Option>
              </Select>
            </Col>
          </Row>
        </fieldset>

        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('MonthlyPayment.secondLegend', {
              defaultValue: 'Property information',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('MonthlyPayment.salePriceLabel', {
                  defaultValue: 'Sale price of property ($)',
                })}
                id="propertyValue"
                name="propertyValue"
                type="number"
                ref={register(validationSchema.propertyValue)}
                error={formatError(errors.propertyValue, validationSchema.propertyValue, t)}
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <Select
                label={t('MonthlyPayment.estimateTipmiLabel', {
                  defaultValue:
                    'Let us estimate property taxes, insurance and private mortgage insurance?',
                })}
                id="estimateTipmi"
                name="estimateTipmi"
                ref={register(validationSchema.estimateTipmi)}
                error={formatError(errors.estimateTipmi, validationSchema.estimateTipmi, t)}
                onChange={setDisabledFields}
              >
                <Select.Option value="Y">
                  {t('MonthlyPayment.estimateTipmiOptions.yes', {
                    defaultValue: 'Yes',
                  })}
                </Select.Option>
                <Select.Option value="N">
                  {t('MonthlyPayment.estimateTipmiOptions.no', {
                    defaultValue: 'No',
                  })}
                </Select.Option>
              </Select>
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('MonthlyPayment.annualPropertyTaxesLabel', {
                  defaultValue: 'Annual property taxes ($)',
                })}
                id="propertyTaxes"
                name="propertyTaxes"
                type="number"
                min="0"
                ref={register(validationSchema.propertyTaxes)}
                error={formatError(errors.propertyTaxes, validationSchema.propertyTaxes, t)}
                disabled={disabledField}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('MonthlyPayment.annualHazardInsuranceLabel', {
                  defaultValue: 'Annual hazard insurance ($)',
                })}
                id="hazardInsurance"
                name="hazardInsurance"
                type="number"
                min="0"
                ref={register(validationSchema.hazardInsurance)}
                error={formatError(errors.hazardInsurance, validationSchema.hazardInsurance, t)}
                disabled={disabledField}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('MonthlyPayment.monthlyPrivateMortgageInsuranceLabel', {
                  defaultValue: 'Monthly private mortgage insurance ($)',
                })}
                id="pmi"
                name="pmi"
                type="number"
                min="0"
                ref={register(validationSchema.pmi)}
                error={formatError(errors.pmi, validationSchema.pmi, t)}
                disabled={disabledField}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <Stack alignment="center" className={styles.Submit}>
          <Button
            type="submit"
            disabled={inProgress || disabled}
            loading={inProgress}
            alt={t('MonthlyPayment.calculateAlt', {
              defaultValue: 'Calculate to view results',
            })}
          >
            {t('MonthlyPayment.calculate', {
              defaultValue: 'Calculate',
            })}
          </Button>

          {alert && alert.type === 'error' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faTimes} className={styles.TimesIcon} />
              {t('MonthlyPayment.couldNotCalculate', {
                defaultValue: 'Sorry, could not calculate',
              })}
            </span>
          )}

          {alert && alert.type === 'success' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faCheck} className={styles.CheckIcon} />
              {t('MonthlyPayment.calculationComplete', {
                defaultValue: 'Calculation complete. See below for results!',
              })}
            </span>
          )}
        </Stack>
      </form>

      <div ref={resultsContainer} />

      {calcResults && (
        <div className={styles.Results}>
          <Heading
            className={styles.ResultsHeading}
            element="h3"
            size={3}
            tabIndex="0"
            forwardedRef={resultsHeader}
          >
            {t('MonthlyPayment.resultHeadline', {
              defaultValue: 'Results',
            })}
          </Heading>
          <p>{calcResults.responseText}</p>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('MonthlyPayment.summaryTable', {
              defaultValue: 'Summary table',
            })}
          </Heading>

          <table summary="Summary Table" className={styles.ResultsTable} role="grid">
            <caption>
              {t('MonthlyPayment.summaryTable', {
                defaultValue: 'Summary table',
              })}
            </caption>
            <tbody>
              <tr>
                <th colSpan="2" className="section">
                  {t('MonthlyPayment.loanInformation', {
                    defaultValue: 'Loan information',
                  })}
                </th>
              </tr>
              <tr>
                <th scope="row">
                  {t('MonthlyPayment.LoanAmount', {
                    defaultValue: 'Loan amount',
                  })}
                </th>
                <td>${formatNumber(storedFormData.loanAmount)}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('MonthlyPayment.AnnualInterestRate', {
                    defaultValue: 'Annual interest rate',
                  })}
                </th>
                <td>{storedFormData.interestRate}%</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('MonthlyPayment.numberOfMonths', {
                    defaultValue: 'Number of months',
                  })}
                </th>
                <td>{storedFormData.termMonths}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('MonthlyPayment.monthlyPrincipalInterestPayment', {
                    defaultValue: 'Monthly principal and interest payment',
                  })}
                </th>
                <td>{calcResults.pandi}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('MonthlyPayment.monthlyPropertyTaxes', {
                    defaultValue: 'Monthly property taxes',
                  })}
                </th>
                <td>{calcResults.monthlyTaxes}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('MonthlyPayment.monthlyHazardInsurance', {
                    defaultValue: 'Monthly hazard insurance',
                  })}
                </th>
                <td>{calcResults.monthlyInsurance}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('MonthlyPayment.monthlyPMI', {
                    defaultValue: 'Monthly PMI (if applicable)',
                  })}
                </th>
                <td>{calcResults.monthlyPmi}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('MonthlyPayment.totalMonthlyPayment', {
                    defaultValue:
                      'Total monthly payment (including taxes, insurance, and PMI if applicable)',
                  })}
                </th>
                <td>{calcResults.pandiTipmi}</td>
              </tr>
            </tbody>
          </table>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('MonthlyPayment.graph', {
              defaultValue: 'Graph',
            })}
          </Heading>
          <img
            src={calcResults.chartUrl}
            alt={t('MonthlyPayment.graphOfResults', {
              defaultValue: 'Graph of results',
            })}
          />

          {calcResults.year && calcResults.year.length > 0 && (
            <Fragment>
              <Heading className={styles.ResultsHeading} element="h3" size={3}>
                {t('MonthlyPayment.amortizationTable', {
                  defaultValue: 'Amortization Table',
                })}
              </Heading>

              <table
                summary={t('MonthlyPayment.amortizationTable', {
                  defaultValue: 'Amortization Table',
                })}
                className={styles.ResultsTable}
                role="grid"
              >
                <caption>
                  {t('MonthlyPayment.amortizationTable', {
                    defaultValue: 'Amortization Table',
                  })}
                </caption>

                <tbody>
                  <tr>
                    <th>
                      {storedFormData.amortization === '2'
                        ? t('MonthlyPayment.year', {
                            defaultValue: 'Year',
                          })
                        : t('MonthlyPayment.month', {
                            defaultValue: 'Month',
                          })}
                    </th>
                    <th>
                      {t('MonthlyPayment.beginningBalance', {
                        defaultValue: 'Beginning Balance',
                      })}
                    </th>
                    <th>
                      {t('MonthlyPayment.principal', {
                        defaultValue: 'Principal',
                      })}
                    </th>
                    <th>
                      {t('MonthlyPayment.interest', {
                        defaultValue: 'Interest',
                      })}
                    </th>
                    <th>
                      {t('MonthlyPayment.payment', {
                        defaultValue: 'Payment',
                      })}
                    </th>
                    <th>
                      {t('MonthlyPayment.endingBalance', {
                        defaultValue: 'Ending Balance',
                      })}
                    </th>
                  </tr>

                  {calcResults.year.map(year => (
                    <tr key={year.period}>
                      <th scope="row">{year.period}</th>
                      <td>{year.beginningBalance}</td>
                      <td>{year.principal}</td>
                      <td>{year.interest}</td>
                      <td>{year.payment}</td>
                      <td>{year.endingBalance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Fragment>
          )}
        </div>
      )}
    </div>
  )
}

export { MonthlyPaymentCalculator }
export default MonthlyPaymentCalculator
